import { extractAnswerFromJSON } from "@/components/document-chat/utils";

/**
 * Processes a streaming token and handles source extraction
 *
 * Handles the complex logic of:
 * - Accumulating stream buffer
 * - Detecting and parsing SOURCE tags
 * - Extracting JSON answers
 * - Managing sources metadata
 *
 * @param currentBuffer - The current raw stream buffer
 * @param token - The new token to append
 * @param currentSources - The current sources metadata
 * @returns Updated buffer, content, and sources
 */
export function processStreamToken(
  currentBuffer: string,
  token: string,
  currentSources: any[] | null
): {
  rawStreamBuffer: string;
  streamingContent: string;
  sourcesFromMetadata: any[] | null;
} {
  const newRawBuffer = currentBuffer + token;

  // Check if we have sources in the stream
  if (newRawBuffer.includes("<SOURCE>")) {
    const [contentPart, sourcePart] = newRawBuffer.split("<SOURCE>");

    // If we have a complete source block
    if (sourcePart && sourcePart.includes("</SOURCE>")) {
      const [sourceContent, remainingContent] = sourcePart.split("</SOURCE>");

      // Try to parse sources
      let parsedSources = null;
      try {
        parsedSources = JSON.parse(sourceContent.trim());
      } catch {
        // If parsing fails, continue without sources
      }

      // Extract answer from JSON if needed
      const extractedContent = extractAnswerFromJSON(contentPart + (remainingContent || ""));

      return {
        rawStreamBuffer: newRawBuffer,
        streamingContent: extractedContent,
        sourcesFromMetadata: parsedSources || currentSources,
      };
    }

    // Source tag started but not complete yet
    const extractedContent = extractAnswerFromJSON(contentPart);
    return {
      rawStreamBuffer: newRawBuffer,
      streamingContent: extractedContent,
      sourcesFromMetadata: currentSources,
    };
  }

  // No source tags, check if we need to extract JSON answer
  const extractedContent = extractAnswerFromJSON(newRawBuffer);
  return {
    rawStreamBuffer: newRawBuffer,
    streamingContent: extractedContent,
    sourcesFromMetadata: currentSources,
  };
}
